import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Box,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Link
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, Add as AddIcon } from '@mui/icons-material';
import API_BASE_URL from '../config';
import './Presets.css';


const Presets = () => {
  const [presets, setPresets] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');
  const [openDialog, setOpenDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    fetchPresets();
  }, []);

  useEffect(() => {
      const checkLocalStorage = () => {
        const storedPrimaryColor = localStorage.getItem('primaryColor');
        const storedSecondaryColor = localStorage.getItem('secondaryColor');
        const storedDarkMode = localStorage.getItem('darkMode') === 'true';
  
        if (storedPrimaryColor !== primaryColor) {
          setPrimaryColor(storedPrimaryColor);
        }
        if (storedSecondaryColor !== secondaryColor) {
          setSecondaryColor(storedSecondaryColor);
        }
        if (storedDarkMode !== darkMode) {
          setDarkMode(storedDarkMode);
          setTextColor(storedDarkMode? '#ffffff' : '#000000');
        }
      };
  
      checkLocalStorage();
      const intervalId = setInterval(checkLocalStorage, 200);
  
      return () => clearInterval(intervalId);
    }, [primaryColor, secondaryColor, darkMode]);

  const fetchPresets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/presets`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const presetsData = await response.json();
      setPresets(presetsData);
      setError(null);
    } catch (error) {
      console.error('Error fetching presets:', error);
      setError('Failed to fetch presets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPreset = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewPresetName('');
    setNameError('');
  };

  const handleNewPresetNameChange = (event) => {
    const name = event.target.value;
    setNewPresetName(name);
    if (presets.some(preset => preset.preset_name.toLowerCase() === name.toLowerCase())) {
      setNameError('A preset with this name already exists');
    } else {
      setNameError('');
    }
  };

  const handleCreatePreset = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/new-preset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ preset_name: newPresetName })
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to create preset');
      }

      handleCloseDialog();
      window.location.reload();
    } catch (error) {
      console.error('Error creating preset:', error);
      // Handle error (e.g., show error message to user)
    }
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePresetClick = (presetId) => {
    navigate(`/presets/${presetId}`);
  };

  if (loading) {
    return <div style={{ color: primaryColor }}>Loading presets...</div>;
  }

  if (error) {
    return <div style={{ color: primaryColor }}>Error: {error}</div>;
  }

  return (
    <div className={`presets ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: secondaryColor,
        overflow: 'hidden',
      }}>
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {['PRESET NAME', 'ACTIONS'].map((header) => (
                  <TableCell 
                    key={header} 
                    sx={{ 
                      backgroundColor: `${darkMode ? '#666666' : '#aaaaaa'} !important`,
                      color: `${textColor} !important`,
                      textAlign: 'center',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {presets.length > 0 ? (
                presets
                  .sort((a, b) => (a?.preset_name || '').localeCompare(b?.preset_name || ''))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((preset, index) => (
                    <TableRow 
                      key={preset.id} 
                      hover 
                      sx={{ 
                        backgroundColor: index % 2 ? secondaryColor : (darkMode ? '#262626' : '#f5f5f5'),
                        '&:hover': {
                          backgroundColor: darkMode ? '#333333' : '#e0e0e0',
                        },
                        height: 'auto',
                      }}
                    >
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        {preset.preset_name}
                      </TableCell>
                      <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                        <IconButton onClick={(e) => { e.stopPropagation(); handlePresetClick(preset.id); }} sx={{ color: textColor }}>
                          <OpenInNewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={3} 
                    align="center" 
                    sx={{ 
                      color: `${textColor} !important`, 
                      padding: '16px',
                      backgroundColor: darkMode ? '#262626' : '#f5f5f5',
                    }}
                  >
                    No presets available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          rowsPerPageOptions={[50,100,200]}
          component="div"
          count={presets.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: textColor,
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select, .MuiTablePagination-selectIcon': {
              color: `${textColor} !important`,
            },
            '.MuiTablePagination-actions': {
              color: textColor,
              '& .MuiIconButton-root.Mui-disabled': {
                color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              },
            },
          }}
        />
      </Paper>
      <Fab
        variant="extended"
        color="primary"
        aria-label="add preset"
        onClick={handleAddPreset}
        sx={{
          position: 'absolute',
          bottom: 72,
          right: 16,
          backgroundColor: primaryColor,
          color: textColor,
          '&:hover': {
            backgroundColor: darkMode ? '#555' : '#e0e0e0',
          },
        }}
      >
        <AddIcon sx={{ mr: 1 }} />
        Add Preset
      </Fab>
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          style: {
            backgroundColor: darkMode ? '#333' : '#fff',
            color: textColor,
          },
        }}
      >
        <DialogTitle sx={{ color: textColor }}>Add New Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Preset Name"
            type="text"
            fullWidth
            variant="outlined"
            inputProps={{ maxLength: 255 }}
            value={newPresetName}
            onChange={handleNewPresetNameChange}
            error={!!nameError}
            helperText={nameError}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: primaryColor,
                },
              },
              '& .MuiInputLabel-root': {
                color: textColor,
              },
              '& .MuiInputBase-input': {
                color: textColor,
              },
              '& .MuiFormHelperText-root': {
                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: textColor }}>Cancel</Button>
          <Button 
            onClick={handleCreatePreset} 
            disabled={!newPresetName || !!nameError}
            sx={{
              color: !newPresetName || !!nameError 
                ? (darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)') 
                : (darkMode ? '#4caf50' : '#45a049'),
              backgroundColor: !newPresetName || !!nameError 
                ? 'transparent'
                : (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)'), 
              '&:hover': {
                backgroundColor: !newPresetName || !!nameError 
                  ? 'transparent'
                  : (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)'),
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Presets;